import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import { createColumns } from "../../../helpers";
import { DATATABLE_CUSTOM_STYLES } from "../../../constants/datatableStyles";
import PageTitle from "../../../Components/UI/PageTitle";
import EyeDetailsIcon from "../../../Components/Svgs/EyeDetailsIcon";
import RowLoader from "../../../Components/UI/RowLoader";
import ErrorAnimationComponent from "../../../assets/lottie/ErrorAnimation/ErrorAnimationComponent";
import LightButton from "../../../Components/UI/LightButton";
import CustomPagination from "../../../Components/CustomPagination";
import DeleteIcon from "../../../Components/Svgs/DeleteIcon";
import EditIcon from "../../../Components/Svgs/EditIcon";
import {
  deleteInstructorData,
  getInstructorsListForAdmin,
} from "../../../apis";
import { convertBooleanValues, transformKeys } from "../../../_helper";
import Swal from "sweetalert2";

const ListInstructors = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [instructorsData, setInstructorsData] = useState([]);
  const [paginator, setPaginator] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const [sortKey, setSortKey] = useState(
    new URLSearchParams(location.search).get("sortKey") || ""
  );
  const [sortDirection, setSortDirection] = useState(
    new URLSearchParams(location.search).get("sortDirection") || ""
  );
  const [currentPage, setCurrentPage] = useState(
    parseInt(new URLSearchParams(location.search).get("page"), 10) || 1
  );
  const handleBooleanValues = convertBooleanValues(instructorsData, [
    "isActive",
  ]);
  const handleInstructorsData = handleBooleanValues?.map(
    (d) => transformKeys(d) || {}
  );

  useEffect(() => {
    setIsLoading(true);
    getInstructorsListForAdmin(currentPage, sortKey, sortDirection)
      .then((response) => {
        setInstructorsData(response.data.data);
        setPaginator({
          current_page: response.data.current_page,
          last_page: response.data.last_page,
        });
      })
      .catch((error) => {
        setIsError(
          error?.response?.data?.message ||
            "Error while fetching instructors data"
        );
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [sortKey, sortDirection, currentPage]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (sortKey) params.set("sortKey", sortKey);
    if (sortDirection) params.set("sortDirection", sortDirection);
    navigate({ search: params.toString() });
  }, [sortKey, sortDirection, navigate]);

  const handleSortChange = (column, direction) => {
    setSortKey(column.sortKey);
    setSortDirection(direction);
  };

  const filterAndSortData = () => {
    return handleInstructorsData;
  };
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const hiddenColumns = ["id"];

  const handleDeleteLesson = (instructorId) => {
    if (!instructorId) return;

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: () => {
        return deleteInstructorData(instructorId)
          .then((response) => {
            return response.data;
          })
          .catch((error) => {
            Swal.showValidationMessage(
              error?.response?.data?.message ||
                "Failed to delete the instructor data"
            );
            console.error("Error deleting instructor data:", error);
          });
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire("Deleted!", "The instructor has been deleted.", "success");

        // refetch data after deleting the instructor
        getInstructorsListForAdmin(currentPage, sortKey, sortDirection)
          .then((response) => {
            setInstructorsData(response.data.data);
            setPaginator({
              current_page: response.data.current_page,
              last_page: response.data.last_page,
            });
          })
          .catch((error) => {
            console.error("Error fetching list instructors data", error);
          });
      }
    });
  };
  const customColumns = [
    {
      name: "Details",
      selector: (row) => row.name + " - custom",
      ui: (row) => (
        <div className="tw-flex tw-gap-1">
          <Link to={`/instructor-details/${row.id}`}>
            <EyeDetailsIcon width="30px" height="30px" />
          </Link>
        </div>
      ),
    },
    {
      name: "Edit",
      selector: (row) => row.name + " - custom",
      ui: (row) => (
        <div className="tw-flex tw-gap-1">
          <Link to={`/edit-instructor-data/${row.id}`}>
            <EditIcon width="22px" height="30px" />
          </Link>
        </div>
      ),
    },
    {
      name: "Delete",
      selector: (row) => row.name + " - custom",
      ui: (row) => (
        <button
          className="!tw-m-0 !tw-p-0"
          onClick={() => handleDeleteLesson(row.id)}
        >
          <DeleteIcon width="17px" />
        </button>
      ),
    },
  ];
  const columns = useMemo(() => {
    return createColumns(
      handleInstructorsData,
      customColumns,
      hiddenColumns,
      true
    );
  }, [handleInstructorsData]).map((col) => ({
    ...col,
    sortable: ["Num", "English Name", "Created At", "Num Of Courses"].includes(
      col.name
    ),
    width: ["Num", "Edit", "Delete", "Details"].includes(col.name)
      ? "70px"
      : "1fr",
    cell:
      col.name === "Image"
        ? (row) =>
            row.image ? (
              <img
                src={row.image}
                alt="Instructor"
                className="tw-w-14 tw-h-14 tw-rounded-full tw-object-cover"
              />
            ) : (
              "No Image"
            )
        : col.cell,
  }));

  if (isError) return <ErrorAnimationComponent message={isError} />;

  return (
    <>
      <div className="tw-flex max-md:tw-flex-col justify-content-between tw-items-center">
        <PageTitle title="Instructors Management" />
        <LightButton
          hoverColor="hover:tw-text-blueLight"
          text={"Add New Instructor"}
          icon={<span className="material-icons">add</span>}
          to={"/add-new-instructor"}
        />
      </div>
      <div className="bg-white flex flex-col mt-4">
        <div className="bg-white shadow-lg rounded-lg flex flex-col p-2 gap-2 mb-5">
          <DataTable
            columns={columns}
            data={filterAndSortData()}
            customStyles={DATATABLE_CUSTOM_STYLES}
            progressPending={isLoading}
            onSort={handleSortChange}
            progressComponent={<RowLoader />}
            sortServer
          />
          <div className="flex justify-center mt-4">
            <CustomPagination
              totalPages={paginator.last_page || 1}
              currentPage={paginator.current_page || 1}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ListInstructors;
